import { getTagSearchResultsV2 } from "@/apis/taxonomy/route";
import {
  TagSearchResultsItemV2,
  TagSearchResultsResponseV2,
} from "@/apis/taxonomy/types";
import Input from "@/components/common/input";
import { SearchIcon } from "@/components/icons/search";
import { useMappingStore } from "@/stores/mapping";
import { Button } from "@/styles/ui/button";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useTableId } from "../hooks";
import { useMappingSearchResults } from "../retrieval-results/hooks";
import SearchResultLoading from "./search-result-loading";
import SearchResults from "./search-results";

/**
 * 선택된 셀의 서버 매핑 상태(이미 매겨진 태그·기간)를 store(mappedInfos)로 동기화한다.
 *
 * 이전에는 검색 결과 항목 200개가 각자 useMappingInfos → useMappingSearchResults를
 * 호출하며 이 동기화를 중복 실행했다. 무거운 훅을 항목에서 분리(SelectedAddTagButton)
 * 하면서, 검색 패널이 열려 있는 동안 동기화가 끊기지 않도록 그 책임을 패널 레벨의
 * 이 컴포넌트 1개로 옮겼다. (동기화는 useMappingSearchResults 내부의
 * useUpdateStatesFromServer가 수행한다.)
 *
 * UI를 그리지 않으므로(return null) 이 컴포넌트의 재렌더가 목록으로 전파되지 않는다.
 */
const SelectedCellMappedInfoSync = () => {
  const dimension = useMappingStore((state) => state.dimension.dimension);
  useMappingSearchResults(dimension || "LINE_ITEM");
  return null;
};

const SearchTagSection = ({
  onDeleteButtonClick,
  className,
}: {
  onDeleteButtonClick: () => void;
  className?: string;
}) => {
  const { dimension } = useMappingStore((state) => state.dimension);
  const [query, setQuery] = useState("");
  const [prevQuery, setPrevQuery] = useState("");
  const { data: tableInfo } = useTableId();
  const [selectedTag, setSelectedTag] = useState<TagSearchResultsItemV2 | null>(
    null,
  );
  const [selectedContainerTag, setSelectedContainerTag] =
    useState<TagSearchResultsItemV2 | null>(null);

  // TODO the reset function!
  const { mutate, isPending, isError, data, reset } = useMutation<
    TagSearchResultsResponseV2,
    Error,
    void
  >({
    mutationFn: () => {
      return getTagSearchResultsV2(
        tableInfo?.workbookId!,
        query,
        tableInfo?.tableId!,
        dimension || "LINE_ITEM",
      );
    },
  });

  useEffect(() => {
    reset();
    setQuery("");
    setPrevQuery("");
    setSelectedTag(null);
    setSelectedContainerTag(null);
  }, [dimension]);

  if (isError) {
    return <div>Error</div>;
  }

  const handleSearch = () => {
    const workbookId = tableInfo?.workbookId;
    if (!workbookId || !query.trim()) return;
    setPrevQuery(query);
    mutate();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      handleSearch();
    }
  };

  return (
    <div className={className}>
      <SelectedCellMappedInfoSync />
      <div className="py-3 px-4 w-full">
        <div className="flex flex-col gap-[8px]">
          <div className="flex justify-start items-center gap-2">
            <Button
              variant="ghost"
              className="p-[6px] w-[28px] h-[28px]"
              onClick={onDeleteButtonClick}
            >
              <ArrowLeft className="cursor-pointer" />
            </Button>
            <div className="label-1-normal-medium">검색</div>
          </div>
          <div className="flex items-center gap-2 self-stretch w-full">
            <Input
              placeholder="태그 검색"
              size="large"
              className="rounded-[8px] border border-[#E5E5E5] shadow-sm"
              icon={<SearchIcon />}
              onKeyDown={handleKeyDown}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {data?.tags !== undefined && data?.tags.length > 0 && (
            <div className="caption-1-semibold text-blue-500">
              총{" "}
              {data?.tags.reduce((acc, result) => acc + result.items.length, 0)}
              개의 검색 결과
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isPending ? (
          <SearchResultLoading className="py-[275px]" />
        ) : (
          <SearchResults
            prevQuery={prevQuery}
            data={data}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            selectedContainerTag={selectedContainerTag}
            setSelectedContainerTag={setSelectedContainerTag}
            onButtonClickCommonAction={onDeleteButtonClick}
          />
        )}
      </div>
    </div>
  );
};

export default SearchTagSection;
